/*
 * Shared display primitives used by the Pro Carer Circle Hub and the
 * focused Shift Kit launcher page. Extracted so a single copy backs
 * both surfaces — no duplication.
 */
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  Sparkles,
  Edit3,
  Printer,
  Download,
  ExternalLink,
  FileText,
} from "lucide-react";

export const SECTION_HEADING =
  "font-display text-2xl md:text-[1.75rem] font-semibold tracking-tight";

export const Para = ({ children }) => (
  <p className="text-ink-muted leading-relaxed mt-4 first:mt-0">{children}</p>
);

export const H3 = ({ children }) => (
  <h3 className="font-display text-lg md:text-xl font-semibold text-ink mt-6 first:mt-0">
    {children}
  </h3>
);

export const H4 = ({ children }) => (
  <h4 className="font-display text-base font-semibold text-ink mt-5">{children}</h4>
);

export const Bullets = ({ items, icon: I = CheckCircle2, color = "text-teal" }) => (
  <ul className="mt-3 space-y-2">
    {items.map((it) => (
      <li key={it} className="flex gap-3 text-ink-muted leading-relaxed">
        <I className={`w-5 h-5 mt-0.5 shrink-0 ${color}`} strokeWidth={1.75} aria-hidden="true" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

export const Callout = ({ tone = "teal", title, children, icon: I = Sparkles }) => {
  const toneClasses = {
    teal: "border-teal/20 bg-teal/5",
    purple: "border-purple/20 bg-purple/5",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    slate: "border-slate-200 bg-[#f7f7f3]",
  }[tone];
  const iconColor = {
    teal: "text-teal",
    purple: "text-purple",
    amber: "text-amber-700",
    slate: "text-ink",
  }[tone];
  return (
    <div className={`mt-5 rounded-2xl border p-5 md:p-6 ${toneClasses}`}>
      <div className="flex items-start gap-3">
        <I className={`w-5 h-5 shrink-0 mt-0.5 ${iconColor}`} strokeWidth={1.75} aria-hidden="true" />
        <div className="flex-1">
          {title && <div className="font-semibold text-ink mb-1">{title}</div>}
          <div className="text-sm md:text-base text-ink-muted leading-relaxed">{children}</div>
        </div>
      </div>
    </div>
  );
};

// Standard "Make a copy" note shown beside a fillable Google Drive form
export const MakeACopyNote = () => (
  <p className="mt-3 text-xs text-ink-soft leading-relaxed">
    <strong className="text-ink">Important — please make your own copy before using.</strong> Open the document and
    choose "Make a copy", save it into your own Drive, then rename clearly (e.g. <em>Daily Care Log – Mrs Smith – 17
    June 2026</em>). Edit your own copy only — keep the original template clean.
  </p>
);

// Drive download button — "Interactive" (purple) or "Printable" (teal).
// Native JCC forms (href starting with "/forms/") open inside the app via
// react-router Link, never in an external tab — everything else (Google
// Drive) keeps opening in a new tab as before.
export function DriveLink({ href, label, variant = "interactive", testId }) {
  const variants = {
    interactive: {
      cls: "border-purple/30 text-purple hover:bg-purple/5",
      Icon: Edit3,
      defaultLabel: "Open interactive form",
    },
    printable: {
      cls: "border-teal/30 text-teal hover:bg-teal/5",
      Icon: Printer,
      defaultLabel: "Printable version",
    },
    download: {
      cls: "border-purple/30 text-purple hover:bg-purple/5",
      Icon: Download,
      defaultLabel: "Download PDF",
    },
    native: {
      cls: "border-teal/30 text-teal hover:bg-teal/5",
      Icon: FileText,
      defaultLabel: "Open in JCC Forms",
    },
  };
  const isNative = typeof href === "string" && href.startsWith("/forms/");
  const v = variants[isNative ? "native" : variant] || variants.download;

  if (isNative) {
    return (
      <Link
        to={href}
        data-testid={testId}
        className={`inline-flex items-center gap-2 rounded-full border-2 ${v.cls} bg-white px-4 py-2 text-sm font-semibold transition`}
      >
        <v.Icon className="w-4 h-4" strokeWidth={1.75} />
        {label || v.defaultLabel}
      </Link>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className={`inline-flex items-center gap-2 rounded-full border-2 ${v.cls} bg-white px-4 py-2 text-sm font-semibold transition`}
    >
      <v.Icon className="w-4 h-4" strokeWidth={1.75} />
      {label || v.defaultLabel}
      <ExternalLink className="w-3.5 h-3.5 opacity-70" strokeWidth={2} />
    </a>
  );
}
